// src/pages/internal/cadastroHosts/cadastro/index.js
import { Box } from "@mui/material";
import NetworkTable from "../dataTable";
import { useOutletContext } from "react-router-dom";
import { useMemo } from "react";

const IP_REGEX =
	/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}$/i;

const ipValidator = (value, callback) => {
	if (!value) {
		callback(true);
		return;
	}
	callback(IP_REGEX.test(value));
};

export default function CadastroTable() {
	// Recupera os setters do Contexto
	const {
		hostsCadastrados,
		templates,
		setOpenEditHostModal,
		setEditingHostData,
		handleRequestDelete,
		proxys = [],
		buscarHost,
		hostClientsMap = {},
	} = useOutletContext();

	const statusColumn = false;

	// Mapeamento de Proxys
	const { proxyNames, proxyMap } = useMemo(() => {
		const names = ["Sem Proxy"];
		const map = { 0: "Sem Proxy" };

		if (Array.isArray(proxys)) {
			proxys.forEach((p) => {
				names.push(p.name);
				map[p.proxyid] = p.name;
			});
		}
		return { proxyNames: names, proxyMap: map };
	}, [proxys]);

	// --- LÓGICA DE FILTRO E PREPARAÇÃO DOS DADOS ---
	const rowsForDisplay = useMemo(() => {
		// Mapeia os dados enriquecendo com proxy, associations e clientCount
		const mappedRows = hostsCadastrados.map((host) => {
			const proxyId = host.proxy || "0";
			const proxyDisplayName =
				proxyMap[proxyId] || (proxyId === "0" ? "Sem Proxy" : proxyId);

			// Associações de clientes/serviços do mapa recebido do pai
			const associations = hostClientsMap?.[host?.id] ?? [];
			const clientCount = new Set(associations.map((a) => a?.clienteId)).size;

			return {
				...host,
				proxy: proxyDisplayName,
				associations,
				clientCount,
			};
		});

		// Se não houver busca, retorna tudo
		if (!buscarHost) return mappedRows;

		// Filtra pelas colunas solicitadas
		const searchLower = buscarHost.toLowerCase();

		return mappedRows.filter((row) => {
			// Helper para verificar se o valor contém a busca de forma segura
			const check = (val) =>
				val && String(val).toLowerCase().includes(searchLower);

			return (
				check(row.id) ||
				check(row.hostid) ||
				check(row.nome) || // Host
				check(row.ip) || // IP
				check(row.comunidade) || // Comunidade
				check(row.portaSnmp) || // Porta SNMP
				check(row.fabricante) || // Fabricante
				check(row.modelo) || // Modelo
				check(row.userSsh) || // User SSH
				check(row.senhaSsh) || // Senha SSH
				check(row.portaSsh) || // Porta SSH
				check(row.proxy)
			);
		});
	}, [hostsCadastrados, proxyMap, buscarHost, hostClientsMap]);

	const { manufacturerList, equipmentMap } = useMemo(() => {
		const map = {};
		const list = [];
		if (Array.isArray(templates)) {
			templates.forEach((item) => {
				const key = Object.keys(item)[0];
				if (key) {
					list.push(key);
					map[key] = item[key]?.map((modelObj) => modelObj.modelo) || [];
				}
			});
		}
		return { manufacturerList: list, equipmentMap: map };
	}, [templates]);

	const tableColumns = useMemo(() => {
		const cols = [
			{
				data: "id",
				type: "text",
				minWidth: 40,
				focusable: false,
				readOnly: true,
			}, // Adicionado readOnly no ID por segurança
			{ data: "nome", type: "text", width: 150 },
			{ data: "ip", validator: ipValidator, width: 120 },
			{ data: "checks.ping", readOnly: true, focusable: false, width: 70 }, // Nova coluna Ping
			{ data: "checks.snmp", readOnly: true, focusable: false, width: 70 }, // Nova coluna SNMP
			{ data: "comunidade", type: "text" },
			{ data: "portaSnmp", type: "text", width: 90 },
			{
				data: "fabricante",
				type: "dropdown",
				source: manufacturerList,
				width: 130,
			},
			{ data: "modelo", type: "dropdown", source: [], width: 130 },
			{ data: "userSsh", type: "text", width: 100 },
			{ data: "senhaSsh", type: "text", width: 80 }, // Cuidado ao exibir senha em texto plano
			{ data: "portaSsh", type: "text", width: 100 },
			{ data: "proxy", type: "dropdown", source: proxyNames, width: 130 },
		];

		if (statusColumn) {
			cols.push({
				data: "error_msg",
				type: "text",
				width: 100,
				wordWrap: true,
			});
		}
		cols.push({
			data: "actions",
			readOnly: true,
			focusable: false,
			width: 120,
			className: "htCenter",
		});

		return cols;
	}, [statusColumn, manufacturerList, proxyNames]); // Adicionado proxyNames

	const handleEditRow = (rowData) => {
		setEditingHostData(rowData);
		setOpenEditHostModal(true);
	};

	const handleDeleteRow = (rowData) => {
		const idToDelete = rowData.id || rowData.hostid || rowData.hostId;
		handleRequestDelete(idToDelete);
	};

	return (
		<Box sx={{ width: "100%" }}>
			<NetworkTable
				rows={rowsForDisplay}
				columns={tableColumns}
				equipmentMap={equipmentMap}
				onEditRow={handleEditRow}
				onDeleteRow={handleDeleteRow}
			/>
		</Box>
	);
}
